import {
  WixPatternsContainer,
  FormPageState,
  ReportBI,
  TaskState,
} from '@wix/bex-core';
import { action, computed, makeObservable, observable } from 'mobx';
import { UseFormReturn, FieldValues } from 'react-hook-form';
import { EntityPageStateBIReporter } from './EntityPageStateBIReporter';
import { EventEmitter, TypedEmitter } from '@wix/bex-core/events';

type OnSaveParams = {
  widgetsFormData: { [key: string]: any };
};

export interface EntityPageStateParams<T, V extends FieldValues = FieldValues> {
  /**
   * A function that fetches the entity data.
   * @returns a promise that resolves to an object containing the entity.
   */
  fetch: () => Promise<{ entity: T | undefined }>;

  /**
   * A function to handle the save action.
   * @returns promise that resolves to an object that may contain a 'successToastText' string, or it may resolve to void.
   */
  onSave: (
    params: OnSaveParams,
  ) => Promise<{ successToastText?: string } | void>;

  /**
   * An optional function to display an error toast message when a save operation fails.
   * @returns an object with an optional message and a `tryAgain` flag, or it can directly return a string message.
   */
  saveErrorToast?: (
    err: unknown,
  ) => { message?: string; tryAgain?: boolean } | string;

  /**
   * An object representing the form state and methods, typically returned by a useForm hook.
   */
  form: UseFormReturn<V>;

  /**
   * An object representing the container, possibly for dependency injection or component management.
   */
  container: WixPatternsContainer;

  /**
   * A string representing the ID of the parent page.
   */
  parentPageId: string;
}

export interface EntityPageStateInterface {}

export interface EntityPageWidgetState {
  validate: () => Promise<{ isValid: boolean; values?: any }>;
  isDirty: boolean;
}

export class EntityPageState<T, V extends FieldValues = FieldValues>
  implements EntityPageStateInterface
{
  readonly initTask = new TaskState();
  readonly submitTask = new TaskState();

  readonly fetch: EntityPageStateParams<T>['fetch'];
  readonly onSave: EntityPageStateParams<T, V>['onSave'];
  readonly saveErrorToast?: EntityPageStateParams<T>['saveErrorToast'];

  _entity: T | null = null;
  private readonly _form: UseFormReturn<V>;
  private readonly _parentPageId: string;

  readonly container: WixPatternsContainer;
  readonly reportBI: ReportBI;
  readonly bi: EntityPageStateBIReporter<T, V>;

  private _isSubmitSuccessful?: boolean;
  private _isFormDirty?: boolean;

  readonly formPage = new FormPageState();

  readonly events = new EventEmitter() as TypedEmitter<{
    save: () => void;
    saveError: (err: unknown) => void;
  }>;

  constructor(params: EntityPageStateParams<T, V>) {
    this.fetch = params.fetch;
    this.onSave = params.onSave;
    this.saveErrorToast = params.saveErrorToast;
    this._form = params.form;
    this._parentPageId = params.parentPageId;

    this.container = params.container;

    this.reportBI = this.container.createBILogger({
      componentType: 'Page',
      pageType: 'Entity',
      route: window.location.pathname,
    });

    this.bi = new EntityPageStateBIReporter({
      reportBI: this.reportBI,
      entityPageState: this,
    });
    this.bi.init();

    makeObservable(this, {
      handleSubmit: action,
      isFetching: computed,
      _entity: observable.ref,
      setEntity: action,
    });
  }

  init() {
    const { initTask, container } = this;
    const { onBeforeUnload } = container;

    const onBeforeUnloadSubscription = onBeforeUnload?.(
      action((event) => {
        if (
          this._isSubmitSuccessful ||
          !(this._isFormDirty || this.formPage.isDirty)
        ) {
          return;
        }
        event.preventDefault();
        this.bi.reportOnBeforeUnload();
      }),
    );

    initTask.runOnce(async () => {
      const [data] = await Promise.all([
        this.fetch(),
        container.initTask.status.promise,
      ]);

      this.setEntity(data.entity ?? null);
    });

    return () => {
      onBeforeUnloadSubscription?.remove();
    };
  }

  retryFetch() {
    this.initTask.runOnce();
  }

  setEntity(newEntity: T | null) {
    this._entity = newEntity;
  }

  get entity(): T | null {
    return this._entity;
  }

  get isFetching() {
    return this.initTask.status.isLoading || this.initTask.status.isIdle;
  }

  get form() {
    return this._form;
  }

  set isSubmitSuccessful(flag: boolean) {
    this._isSubmitSuccessful = flag;
  }

  set isFormDirty(flag: boolean) {
    this._isFormDirty = flag;
  }

  onCancel() {
    this.bi.reportButtonClick({ ctaName: 'Cancel' });
    this.navigateToParent();
  }

  navigateToParent() {
    this.container.navigateTo?.({
      pageId: this._parentPageId,
    });
  }

  _showErrorToast(err: any, e?: React.BaseSyntheticEvent) {
    const errorToast = this.saveErrorToast?.(err);
    const message =
      typeof errorToast === 'string' ? errorToast : errorToast?.message;
    const showTryAgain =
      typeof errorToast === 'string' ? true : errorToast?.tryAgain !== false;

    this.container.showToast?.({
      type: 'ERROR',
      biName: 'cairo-entity-page-save-error-toast',
      message:
        message ??
        this.container.translate(
          err?.code === 'ERR_NETWORK'
            ? 'cairo.entityPage.saveError-offline.toast.description'
            : 'cairo.entityPage.saveError-technical.toast.description',
        ),
      action: showTryAgain
        ? {
            uiType: 'LINK',
            text: this.container.translate('cairo.toast.retry'),
            onClick: () => {
              this.bi.reportSaveTryAgain();
              this.handleSubmit(e);
            },
            removeToastOnClick: true,
          }
        : undefined,
    });
  }

  _showSuccessToast(successToastText?: string) {
    this.container.showToast?.({
      type: 'SUCCESS',
      biName: 'cairo-entity-page-save-success-toast',
      message:
        successToastText ??
        this.container.translate('cairo.entityPage.success.toast'),
    });
  }
  async handleSubmit(e?: React.BaseSyntheticEvent) {
    if (this.submitTask.status.isLoading) {
      return;
    }

    this.events.emit('save');

    const { isValid, data: extraFields } = await this.formPage.validate();

    if (!isValid) {
      this.bi.reportButtonClick({ ctaName: 'Save', isValid: false });
      return;
    }

    this.submitTask.run(
      async () =>
        new Promise<void>((resolve) =>
          this._form.handleSubmit(
            () => {
              this.bi.reportButtonClick({ ctaName: 'Save', isValid: true });
              return this.onSave({ widgetsFormData: { ...extraFields } })
                .then(({ successToastText } = {}) => {
                  this._showSuccessToast(successToastText);
                  resolve();
                  this.navigateToParent();
                })
                .catch((err) => {
                  this._showErrorToast(err, e);
                  this.container.errorMonitor.captureException(err);
                  this.events.emit('saveError', err);
                })
                .finally(() => {
                  resolve();
                });
            },
            () => {
              this.bi.reportButtonClick({ ctaName: 'Save', isValid: false });
              resolve();
            }, // can be used to report validation errors
          )(e),
        ),
    );

    await this.submitTask.status.promise;
  }
}
